import { useCallback, useEffect, useContext, useState, Dispatch, SetStateAction, FC } from 'react'
import { Alert, AlertDescription, AlertIcon, AlertTitle, Flex, Text, Center, Link, Image } from '@chakra-ui/react'

import { Headphones } from 'components/icons'
import { Loader } from 'components'
import { INSTITUTION_SLUG } from 'constants/system'
import PersonalizationContext from './Context'
import getInstitutionPersonalization from './services/calls/getInstitutionPersonalization'
import { PERSONALIZATION_KEY_STORAGE_NAME } from 'constants/index'
import { getLocalStorage, setLocalStorage } from 'utils/storage/localStorage'

type Props = {
  setDefaultColor: Dispatch<SetStateAction<string>>
  children: JSX.Element
}

function SupportButton() {
  return (
    <Link
      href={
        INSTITUTION_SLUG == 'agsus'
          ? 'https://wa.me/5521971364964?text=Ol%C3%A1%2C+preciso+de+ajuda+com+a+%2APlataforma+Impacto%2A'
          : 'https://wa.me/5512997733752?text=Ol%C3%A1%2C+preciso+de+ajuda+com+a+%2APlataforma+Impacto%2A'
      }
      target="_blank"
      border="1px solid"
      borderColor="borderColor"
      rounded="md"
      p={2}
      bg="bgContent"
      mt={6}
      _hover={{ textDecor: 'none' }}
    >
      <Flex w="300px" p={2} justify="space-between">
        <Flex flexDir="column" justify="center" gap={1}>
          <Text fontSize="sm" fontWeight="bold">
            Equipe de suporte
          </Text>
          <Text fontSize="xs">Responderemos assim que possível</Text>
        </Flex>
        <Center w="80px">
          <Headphones variant="twoTone" boxSize={14} />
        </Center>
      </Flex>
    </Link>
  )
}

const PersonalizationProvider: FC<Props> = ({ setDefaultColor, children }) => {
  const [isLoading, setIsLoading] = useState(true)
  const [personalization, setPersonalization] = useState({})
  const [license, setLicense] = useState({})
  const [withoutAPI, setWithoutAPI] = useState(false)

  const getPersonalization = useCallback(async () => {
    const STORAGE_PERSONALIZATION = getLocalStorage(PERSONALIZATION_KEY_STORAGE_NAME)

    if (STORAGE_PERSONALIZATION) {
      const value = JSON.parse(STORAGE_PERSONALIZATION)
      const sameClient = value?.institution_slug == INSTITUTION_SLUG
      if (sameClient) {
        const updateAt = value?.update_at
        const now = Date.now()
        const timeUntilRequestingAgain = 1000 * 60 * 30

        const isExpired = now - updateAt > timeUntilRequestingAgain
        if (!isExpired) {
          setPersonalization(value)
          setLicense(value?.license)
          const institutionColor = value.module_color
          if (institutionColor) setDefaultColor(institutionColor)
          setDefaultColor(institutionColor)
          return
        }
      }
    }

    const response = await getInstitutionPersonalization()

    if (response?.header?.success) {
      const personalizationInfo = response?.body?.personalization

      //ATENDIMENTO EM CAMPO
      if (INSTITUTION_SLUG == 'treinamento') {
        personalizationInfo.license.attendance.feature = {
          ...response?.body?.personalization.license.attendance.feature,
          houseCall: true,
        }
      } else {
        personalizationInfo.license.attendance.feature = {
          ...response?.body?.personalization.license.attendance.feature,
          houseCall: false,
        }
      }

      // CLASSIFICAÇÃO
      if (
        INSTITUTION_SLUG === 'teste' ||
        INSTITUTION_SLUG === 'treinamento' ||
        INSTITUTION_SLUG === 'caruaru' ||
        INSTITUTION_SLUG === 'boavista'
      ) {
        personalizationInfo.license.attendance.feature = {
          ...response?.body?.personalization.license.attendance.feature,
          contextIsHealth: true,
        }
      } else {
        personalizationInfo.license.attendance.feature = {
          ...response?.body?.personalization.license.attendance.feature,
          contextIsHealth: false,
        }
      }

      //CONSULTA NO CAD SUS
      if (
        INSTITUTION_SLUG == 'teste' ||
        INSTITUTION_SLUG == 'treinamento' ||
        INSTITUTION_SLUG == 'igesdf' ||
        INSTITUTION_SLUG == 'novafriburgo'
      ) {
        personalizationInfo.license.integrated_profile.feature = {
          ...response?.body?.personalization.license.integrated_profile.feature,
          consultationInCadSUS: true,
        }
      } else {
        personalizationInfo.license.integrated_profile.feature = {
          ...response?.body?.personalization.license.integrated_profile.feature,
          consultationInCadSUS: false,
        }
      }

      //FAMÍLIAS
      if (INSTITUTION_SLUG == 'teste' || INSTITUTION_SLUG == 'treinamento') {
        personalizationInfo.license.integrated_profile.feature = {
          ...response?.body?.personalization.license.integrated_profile.feature,
          family: true,
        }
      } else {
        personalizationInfo.license.integrated_profile.feature = {
          ...response?.body?.personalization.license.integrated_profile.feature,
          family: false,
        }
      }

      //LOGIN UNIFICADO
      personalizationInfo.license.login.feature = {
        ...response?.body?.personalization.license.login.feature,
        govBR: false,
        digitalCertificate: false,
        biometry: false,
        qrCode: false,
      }

      // GESTÃO INSTITUCIONAL
      if (INSTITUTION_SLUG == 'agsus' || INSTITUTION_SLUG == 'teste') {
        personalizationInfo.license.admin.feature = {
          ...response?.body?.personalization.license.admin.feature,
          bondWithProfessional: true,
          bondWithPublicAgent: true,
          bondWithCitizen: true,
          access: true,
        }
      } else {
        personalizationInfo.license.admin.feature = {
          ...response?.body?.personalization.license.admin.feature,
          bondWithProfessional: false,
          bondWithPublicAgent: true,
          bondWithCitizen: true,
          access: true,
        }
      }

      // MAPA
      if (INSTITUTION_SLUG == 'boavista' || INSTITUTION_SLUG == 'teste') {
        personalizationInfo.license.geo.settings = {
          map_default_view: {
            lat: 2.8209838467124086,
            lon: -60.67261923703196,
            zoom: 15,
          },
        }
      } else if (INSTITUTION_SLUG == 'agsus') {
        personalizationInfo.license.geo.settings = {
          map_default_view: {
            lat: -15.797866134547803,
            lon: -47.891526507103904,
            zoom: 12,
          },
        }
      } else if (INSTITUTION_SLUG == 'brasil') {
        personalizationInfo.license.geo.settings = {
          map_default_view: {
            lat: -14.2350,
            lon: -51.9253,
            zoom: 4,
          },
        }
      }else if (INSTITUTION_SLUG == 'mocambique') {
        personalizationInfo.license.geo.settings = {
          map_default_view: {
            lat: -18.6657,
            lon: 35.5296,
            zoom: 5,
          },
        }
      }else {
        personalizationInfo.license.geo.settings = {
          map_default_view: {
            lat: -10,
            lon: -55,
            zoom: 11,
          },
        }
      }

      personalizationInfo.license.research = {
        name: 'Avaliações e pesquisas',
        description: 'Descrição do Módulo',
        feature: { access: false },
        status: 'ACTIVE',
      }

      setPersonalization(personalizationInfo)
      setLicense(personalizationInfo.license)
      const institutionColor = response?.body?.personalization?.module_color
      if (institutionColor) setDefaultColor(institutionColor)

      personalizationInfo.update_at = Date.now()
      personalizationInfo.institution_slug = INSTITUTION_SLUG
      setLocalStorage({
        key: PERSONALIZATION_KEY_STORAGE_NAME,
        value: JSON.stringify(personalizationInfo),
      })
    } else {
      setWithoutAPI(true)
    }
  }, [])

  function changePrimaryColor(newColor: string) {
    setDefaultColor(newColor)
  }

  useEffect(() => {
    async function prepare() {
      await getPersonalization()
      setIsLoading(false)
    }
    prepare()
  }, [])

  return (
    <PersonalizationContext.Provider value={{ personalization, license, changePrimaryColor }}>
      {isLoading ? (
        <Loader height="100vh" text="Carregando instituição" />
      ) : withoutAPI ? (
        <Center>
          <Alert
            status="error"
            variant="subtle"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            height="100vh"
          >
            <AlertIcon boxSize="40px" mr={0} />
            <AlertTitle mt={4} mb={1} fontSize="lg">
              Alguma coisa aconteceu...
            </AlertTitle>
            <AlertDescription maxWidth="sm">
              Pedimos desculpas pelo ocorrido. Se o problema persistir, entre em contato com o suporte clicando no botão
              abaixo.
            </AlertDescription>
            <SupportButton />
            <Image
              src="https://app.milanote.com/media/p/images/1TxS1I1Ha3Xb3U/orq/Variante1_PNG.png?v=2&elementId=1TxS1I1Ha3Xb3U"
              alt="Logo da Impacto"
              w="200px"
              mt={6}
            />
          </Alert>
        </Center>
      ) : (
        children
      )}
    </PersonalizationContext.Provider>
  )
}

export const usePersonalization = () => useContext(PersonalizationContext)

export default PersonalizationProvider
